import { Droplets, X } from "lucide-react";
import { Card } from "@/components/kit";
import { calculateVolume, formatGal, formatToEighths, productName, scanActive, tankLabel } from "@/lib/tank";
import { useT } from "@/lib/i18n";
import { waterLimit } from "@/lib/safety";

const Cell = ({ id, label, value, tone }) => (
  <div className={`po-cell ${tone ? `po-${tone}` : ""}`} data-testid={`printout-${id}`}><span className="label">{label}</span><span className="mono po-val">{value}</span></div>
);

// The console's own numbers from the last scan, shown as-is.
export function PrintoutCard({ s, r, patch }) {
  const t = useT();
  if (!scanActive(s)) return null;
  const p = s.scan;
  const model = calculateVolume(s.startInches, s.diameter, s.capacity, s.ends);
  const off = p.gallons > 0 ? model - p.gallons : 0;
  const wet = p.water != null && p.water >= 0.25;
  return (
    <Card testID="printout-card" className="printout-card">
      <div className="po-head">
        <b>{t("From the printout")}</b>
        <span className="card-sub">{[tankLabel(p.label), p.product && t(productName(p.product))].filter(Boolean).join(" · ")}</span>
        <button type="button" className="icon-plain" aria-label={t("Stop using the printout")} onClick={() => patch({ scan: null })} data-testid="printout-clear"><X size={16} /></button>
      </div>
      <div className="po-grid">
        <Cell id="gallons" label={t("Volume")} value={p.gallons > 0 ? `${formatGal(p.gallons)} gal` : "—"} />
        <Cell id="height" label={t("Height")} value={formatToEighths(p.inches)} />
        <Cell id="water" label={t("Water")} tone={p.water != null && p.water >= waterLimit() ? "danger" : wet ? "warn" : "ok"} value={p.water == null ? t("not shown") : formatToEighths(p.water)} />
        <Cell id="ullage" label={t("Room to full")} value={p.ullage != null ? `${formatGal(p.ullage)} gal` : "—"} />
        <Cell id="ullage90" label={t("Room to 90%")} value={p.ullage90 != null ? `${formatGal(p.ullage90)} gal` : p.gallons > 0 ? `${formatGal(Math.max(0, r.cap90 - p.gallons))} gal*` : "—"} />
        {p.temp != null && <Cell id="temp" label={t("Temp")} value={`${p.temp}°F`} />}
      </div>
      {wet && <div className={`drop-warn drop-warn-${p.water >= waterLimit() ? "danger" : "caution"}`} data-testid="printout-water-warning" role="alert"><Droplets size={22} /><b className="drop-warn-head">{t("Water at {w}. Check it with paste before you drop.", { w: formatToEighths(p.water) })}</b></div>}
      {p.ullage90 == null && p.gallons > 0 && <div className="card-sub">{t("* Not on the slip: worked out from this tank's size.")}</div>}
      {p.gallons > 0 && Math.abs(off) > Math.max(25, p.gallons * 0.01) && (
        <div className="card-sub" data-testid="printout-model-diff">{t("The tank shape alone would say {m} gal. The prediction uses the console's {g} gal.", { m: formatGal(model), g: formatGal(p.gallons) })}</div>
      )}
    </Card>
  );
}
